import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Profile from "./components/Profile";
import Login from "./components/Login";
import SignUp from "./components/Signup";
import DigitalCard from "./components/DigitalCard";
import ForgotPassword from "./components/ForgotPassword";
import AppLoader from "./components/AppLoader";
import Company from "./components/company";
import Dashboard from "./components/Admin/dashboard";
import Header from "./components/Header";

import { auth, database } from "./firebase";
import { get, ref } from "firebase/database";
import PaymentForm from "./components/Admin/Pages/PaymentForm";
import SetUserPasswordPage from "./components/SetUserPasswordPage";
import EmailSignature from "./components/EmailSignature";
import AdminEmailSignature from "./components/Admin/Pages/ESignature/AdminEmailSignature";
import RequestForm from "./components/request";
import TrialExpired from "./components/trialExpired";
import ChangePassword from "./components/Admin/ChangePassword";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firstLogin, setFirstLogin] = useState(false);
  useEffect(() => {
    const fetchRole = async (uid) => {
      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);
      setFirstLogin(snapshot.val().isFirstLogin || false);
      return snapshot.exists() ? snapshot.val().role : null;
    };
    // const fetchCompanyId = async (uid) => {
    //   const userRef = ref(database, `users/${uid}`);
    //   const snapshot = await get(userRef);
    //   return snapshot.exists() ? snapshot.val().companyId : null;
    // };
    const fetchCompanyData = async (uid) => {
      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const companyId = userData.companyId;

        if (companyId) {
          const companyRef = ref(database, `companies/${companyId}`);
          const companySnapshot = await get(companyRef);
          if (companySnapshot.exists()) {
            const companyData = companySnapshot.val();

            // Convert expiredAt (assumed to be a timestamp or date string) to a Date object
            const today = new Date();
            const expiredAtDate = new Date(companyData.expiredAt);

            // Check if expiredAt date is in the past
            const isExpired = expiredAtDate < today;

            return { companyId, isExpired };
          }
        }
      }
      return { companyId: null, isExpired: false };
    };

    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        const role = await fetchRole(currentUser.uid);
        const { companyId, isExpired } = await fetchCompanyData(
          currentUser.uid
        );

        setUser(
          role ? { uid: currentUser.uid, role, companyId, isExpired } : null
        );
        // Store user state in localStorage so it's available on page reload
        localStorage.setItem(
          "user",
          JSON.stringify({ uid: currentUser.uid, role })
        );
      } else {
        setUser(null);
        // Remove user state from localStorage when logged out
        localStorage.removeItem("user");
      }
      setLoading(false);
    });

    // Check if user is already logged in from localStorage on initial load
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
    }

    return () => unsubscribe();
  }, []);

  if (loading) return <AppLoader />;

  // Protected Route Wrapper
  const ProtectedRoute = ({ children, allowedRole }) => {
    if (!user) return <Navigate to="/login" replace />;
    if (user.isExpired) return <Navigate to="/trialExpired" replace />;
    if (allowedRole && user.role !== allowedRole) {
      return user.role === "Admin" ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <Navigate to="/profile" replace />
      );
    }
    return children;
  };

  return (
    <Router>
      <div className="bg-white min-h-screen">
        {user && <Header user={user} />}
        <div className="auth-inner">
          <Routes>
            {/* Main Redirect Logic */}
            <Route
              path="/"
              element={
                user ? (
                  user.role === "Admin" ? (
                    firstLogin ? ( // Check if first login
                      <Navigate to="/dashboard/profile" />
                    ) : (
                      <Navigate to="/dashboard" />
                    )
                  ) : (
                    <Navigate to="/profile" />
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Login Route */}
            <Route
              path="/login"
              element={!user ? <Login /> : <Navigate to="/" />}
            />

            {/* Signup & Forgot Password */}
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/setUserPassword" element={<SetUserPasswordPage />} />

            {/* Digital Card & Company */}
            <Route
              path="/digitalCard/:companyName/:userName"
              element={<DigitalCard />}
            />
            <Route path="/company" element={<Company />} />

            {/* Protected Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedRole="User">
                  <Profile user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request"
              element={
                <ProtectedRoute allowedRole="User">
                  <RequestForm user={user} />
                </ProtectedRoute>
              }
            />
            <Route path="/emailSignature" element={<EmailSignature />} />
            <Route
              path="/emailSignaturePanel"
              element={<AdminEmailSignature />}
            />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute allowedRole="Admin">
                  <Dashboard user={user} />
                </ProtectedRoute>
              }
            />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="/payment" element={<PaymentForm />} />
            <Route
              path="/trialExpired"
              element={<TrialExpired user={user} />}
            />
            {/* Catch-All Route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>

          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      </div>
    </Router>
  );
}

export default App;
